import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Ban, KeyRound, Loader2, LogOut, Save, ShieldCheck, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import {
  adminGetUserDetail,
  adminBanUser,
  adminUnbanUser,
  adminResetDevices,
  adminSetAccountStatus,
  adminUpdateUser,
  adminChangeRole,
  adminForceLogout,
  adminDeleteUser,
  adminSendPasswordReset,
  adminSetDeviceActive,
} from "@/lib/admin-security.functions";
import { parseDeviceInfo } from "@/lib/device-id";

export const Route = createFileRoute("/_authenticated/admin/users/$id")({
  head: () => ({ meta: [{ title: "User detail — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <UserDetailPage />
    </AdminOnly>
  ),
});

function UserDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const runDetail = useServerFn(adminGetUserDetail);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => runDetail({ data: { userId: id } }),
  });

  const runBan = useServerFn(adminBanUser);
  const runUnban = useServerFn(adminUnbanUser);
  const runReset = useServerFn(adminResetDevices);
  const runStatus = useServerFn(adminSetAccountStatus);
  const runUpdate = useServerFn(adminUpdateUser);
  const runRole = useServerFn(adminChangeRole);
  const runLogout = useServerFn(adminForceLogout);
  const runDelete = useServerFn(adminDeleteUser);
  const runReset2 = useServerFn(adminSendPasswordReset);
  const runDeviceActive = useServerFn(adminSetDeviceActive);

  const invalidate = () => { refetch(); qc.invalidateQueries({ queryKey: ["admin-users"] }); };

  const doMut = <T,>(fn: () => Promise<T>, ok: string) => async () => {
    try { await fn(); toast.success(ok); invalidate(); } catch (e) { toast.error((e as Error).message); }
  };

  const [notes, setNotes] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [maxDevices, setMaxDevices] = useState<number>(3);

  const profile = data?.profile as Record<string, string | number | boolean | null> | undefined;
  const devices = data?.devices ?? [];
  const events = data?.events ?? [];
  const bans = data?.bans ?? [];
  const adminRole = data?.admin_role ?? "user";
  const isBanned = !!profile?.is_banned;

  // Load initial editable fields once we have data
  if (profile && fullName === "" && (profile.full_name ?? null) !== null && String(profile.full_name)) {
    setFullName(String(profile.full_name));
  }
  if (profile && notes === "" && (profile.admin_notes ?? null) !== null && String(profile.admin_notes)) {
    setNotes(String(profile.admin_notes));
  }
  if (profile && maxDevices === 3 && profile.max_devices != null && Number(profile.max_devices) !== 3) {
    setMaxDevices(Number(profile.max_devices));
  }

  if (isLoading || !profile) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading user…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> All users
          </Link>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">
            {String(profile.full_name ?? "—")}
          </h1>
          <div className="mt-1 text-sm text-muted-foreground">{String(profile.email ?? "")}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={
              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
              (profile.account_status === "active" ? "bg-emerald-500/10 text-emerald-500" :
               profile.account_status === "suspended" ? "bg-amber-500/10 text-amber-500" :
               "bg-red-500/10 text-red-500")
            }>{String(profile.account_status)}</span>
            <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{String(profile.role)}</span>
            {adminRole !== "user" && (
              <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">{adminRole}</span>
            )}
            <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{String(profile.subscription_tier)}</span>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {!isBanned ? (
            <Button variant="destructive" onClick={async () => {
              const r = window.prompt("Ban reason:");
              if (!r?.trim()) return;
              await doMut(() => runBan({ data: { userId: id, reason: r.trim() } }), "User banned")();
            }}><Ban className="me-1.5 h-3.5 w-3.5" /> Ban</Button>
          ) : (
            <Button variant="outline" onClick={doMut(() => runUnban({ data: { userId: id, resetDevices: true } }), "User unbanned")}>
              <ShieldCheck className="me-1.5 h-3.5 w-3.5" /> Unban
            </Button>
          )}
          <Button variant="outline" onClick={doMut(() => runReset({ data: { userId: id } }), "Devices reset")}>Reset devices</Button>
          <Button variant="outline" onClick={doMut(() => runLogout({ data: { userId: id } }), "Sessions terminated")}>
            <LogOut className="me-1.5 h-3.5 w-3.5" /> Force logout
          </Button>
          <Button variant="outline" onClick={doMut(() => runReset2({ data: { email: String(profile.email) } }), "Password reset sent")}>
            <KeyRound className="me-1.5 h-3.5 w-3.5" /> Send reset email
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Editable profile fields */}
        <section className="rounded-2xl border bg-card p-5 lg:col-span-2">
          <h2 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <UserCog className="h-3.5 w-3.5" /> Profile
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Max devices</Label>
              <Input type="number" min={1} max={10} value={maxDevices} onChange={(e) => setMaxDevices(Math.max(1, Math.min(10, Number(e.target.value) || 1)))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Admin notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Internal notes about this user…" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={doMut(() => runUpdate({ data: { userId: id, patch: { full_name: fullName, admin_notes: notes, max_devices: maxDevices } } }), "Profile updated")}>
              <Save className="me-1.5 h-3.5 w-3.5" /> Save
            </Button>
            <Button variant="outline" onClick={doMut(() => runStatus({ data: { userId: id, status: profile.account_status === "suspended" ? "active" : "suspended" } }), "Status updated")}>
              {profile.account_status === "suspended" ? "Reactivate" : "Suspend"}
            </Button>
            <div className="ms-auto flex items-center gap-2">
              <Label className="text-xs">Admin role</Label>
              <select
                className="h-9 rounded-md border bg-background px-2 text-xs"
                defaultValue={adminRole}
                onChange={async (e) => {
                  const v = e.target.value as "user" | "admin" | "superadmin";
                  await doMut(() => runRole({ data: { userId: id, newRole: v } }), "Role updated")();
                }}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
                <option value="superadmin">superadmin</option>
              </select>
            </div>
          </div>
        </section>

        {/* Danger zone */}
        <section className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-red-500">Danger zone</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Permanent user deletion is irreversible and only available to superadmins.
          </p>
          <Button
            variant="destructive"
            className="mt-4 w-full"
            onClick={async () => {
              const confirm = window.prompt(`Type the user's email to confirm permanent deletion:\n${profile.email}`);
              if (!confirm) return;
              try {
                await runDelete({ data: { userId: id, confirmEmail: confirm.trim().toLowerCase() } });
                toast.success("User deleted");
                navigate({ to: "/admin/users" });
              } catch (e) {
                toast.error((e as Error).message);
              }
            }}
          >
            <Trash2 className="me-1.5 h-3.5 w-3.5" /> Delete user permanently
          </Button>
        </section>

        {/* Devices */}
        <section className="rounded-2xl border bg-card p-5 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Devices ({devices.length})</h2>
          {devices.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">No devices registered.</div>
          ) : (
            <ul className="divide-y">
              {devices.map((d) => {
                const info = parseDeviceInfo(String(d.user_agent ?? ""));
                const active = !!d.is_active;
                return (
                  <li key={String(d.id)} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{String(d.device_name ?? info.name)}</div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                        <span>{info.browser} · {info.os}</span>
                        {d.ip_address && <span>{String(d.ip_address)}</span>}
                        {d.location && <span>{String(d.location)}</span>}
                        <span>Last {new Date(String(d.last_seen_at)).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                        {active ? "active" : "inactive"}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={doMut(() => runDeviceActive({ data: { deviceRowId: String(d.id), active: !active } }), active ? "Device deactivated" : "Device reactivated")}
                      >
                        {active ? "Deactivate" : "Reactivate"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Bans history */}
        <section className="rounded-2xl border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Ban history</h2>
          {bans.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">No bans recorded.</div>
          ) : (
            <ul className="space-y-3 text-xs">
              {bans.map((b) => (
                <li key={String(b.id)} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${b.action === "ban" ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                      {String(b.action)}
                    </span>
                    <span className="text-muted-foreground">{new Date(String(b.created_at)).toLocaleString()}</span>
                  </div>
                  {b.reason && <div className="mt-1 text-muted-foreground">{String(b.reason)}</div>}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Events */}
        <section className="rounded-2xl border bg-card p-5 lg:col-span-3">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent events ({events.length})</h2>
          {events.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">No events yet.</div>
          ) : (
            <ul className="divide-y text-sm">
              {events.slice(0, 20).map((e) => (
                <li key={String(e.id)} className="flex items-start justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-muted-foreground">{String(e.event_type)}</span>
                    <span className="ms-2 text-xs text-muted-foreground">{e.ip_address ? `${String(e.ip_address)} · ` : ""}{String(e.user_agent ?? "").slice(0, 80)}</span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{new Date(String(e.created_at)).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
